import React, { useContext, useMemo, useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserContext } from './App';
import ChatSidebar from './ChatSidebar';
import FrontpageFooter from './FrontpageFooter';
import { FaCoins, FaCrown, FaTrophy, FaGift, FaUsers, FaChartBar, FaBars, FaComments, FaTimes, FaBoxOpen, FaSync, FaSyncAlt, FaBomb, FaChartLine, FaDice, FaBell, FaVolumeUp, FaUser, FaLock, FaGamepad, FaCog, FaSignOutAlt, FaHistory, FaGlobeAmericas, FaHeadset, FaBoxes, FaCaretUp, FaCaretDown, FaArrowUp, FaBullseye, FaTicketAlt, FaBox, FaInfoCircle, FaShieldAlt, FaSpinner } from 'react-icons/fa';
import { userAPI, apiHelpers } from './api';
import { GiAncientSword, GiCrossedSwords } from 'react-icons/gi';
import { motion, AnimatePresence } from 'framer-motion';

const BRAND_PURPLE = '#ffb703';
const PURPLE_THEME = '#ffc130';

const LayoutWrapper = ({ children }) => {

  const location = useLocation();
  const navigate = useNavigate();
  const {
    user,
    activeBalance,
    setActiveBalance,
    setIsDepositModalOpen,
    setIsWithdrawModalOpen,
    setIsAuthModalOpen,
    setAuthModalView,
    setIsRedeemModalOpen,
    setIsFreeCasesModalOpen,
    setUser
  } = useContext(UserContext);
  const [isClaimingRakeback, setIsClaimingRakeback] = useState(false);
  const [mutedUsers, setMutedUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [showBalanceMenu, setShowBalanceMenu] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDesktopSidebarCollapsed, setIsDesktopSidebarCollapsed] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [activeRightPanel, setActiveRightPanel] = useState('chat');
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isVaultOpen, setIsVaultOpen] = useState(false);
  const [vaultTab, setVaultTab] = useState('deposit');
  const [isGamesCollapsed, setIsGamesCollapsed] = useState(false);
  const [isProfileCollapsed, setIsProfileCollapsed] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [balancePopups, setBalancePopups] = useState([]);

  const balanceRef = useRef(null);
  const notificationsRef = useRef(null);
  const profileRef = useRef(null);
  const prevBalanceRef = useRef(null);
  const prevBalanceTypeRef = useRef(activeBalance);

  useEffect(() => {
      const handleClickOutside = (event) => {
          if (balanceRef.current && !balanceRef.current.contains(event.target)) {
              setShowBalanceMenu(false);
          }
          if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
              setIsNotificationsOpen(false);
          }
          if (profileRef.current && !profileRef.current.contains(event.target)) {
              setIsProfileMenuOpen(false);
          }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const isTosOrPrivacy = location.search.includes('tab=tos') || location.search.includes('tab=privacy') || location.pathname.includes('/tos') || location.pathname.includes('/privacy');

  const activeKey = useMemo(() => {
    const path = location.pathname;
    const tabSearch = new URLSearchParams(location.search).get('tab');
    if (path.startsWith('/coinflip')) return 'coinflip';
    if (path.startsWith('/jackpot')) return 'jackpot';
    if (path.startsWith('/cases')) return 'cases';
    if (path.startsWith('/case-battles')) return 'case-battles';
    if (path.startsWith('/fairness')) return 'fairness';
    if (path.startsWith('/admin')) return 'admin';
    if (tabSearch === 'rewards') return 'rewards';
    if (tabSearch === 'earn-with-us') return 'earn-with-us';
    if (tabSearch === 'leaderboard') return 'leaderboard';
    if (tabSearch === 'free-coins') return 'free-coins';
    if (tabSearch === 'settings') return 'settings';
    return 'dashboard';
  }, [location.pathname, location.search]);

  const navTo = (path) => navigate(path);

  useEffect(() => {
    // Force both the window and our specific scroll container back to top
    window.scrollTo(0, 0);
    const mainEl = document.getElementById('mainContent');
    if (mainEl) mainEl.scrollTo(0, 0);

    // Close mobile sidebars on route change
    setIsSidebarOpen(false);
  }, [location.pathname, location.search]);

  // Fix Tawk.to positioning so it's not under the Windows taskbar/date-time
  useEffect(() => {
    const interval = setInterval(() => {
      if (window.Tawk_API && window.Tawk_API.onLoad) {
        clearInterval(interval);
        window.Tawk_API.onLoad = function() {
          window.Tawk_API.setAttributes({
            'visibility': {
              'desktop': { 'yOffset': 80 },
              'mobile': { 'yOffset': 90 }
            }
          }, function(error){});
        };
        // Also try direct set if already loaded
        try {
          window.Tawk_API.setAttributes({
            'visibility': {
              'desktop': { 'yOffset': 20, 'xOffset': 360 }, // Move left of chat (340px)
              'mobile': { 'yOffset': 140 }  // Move up high on mobile
            }
          }, function(error){});
        } catch(e) {}
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const currentBalance = user 
    ? (activeBalance === 'promo' ? Number(user.promoBalance || 0) : Number(user.balance || 0))
    : 0;

  // Detect balance changes and show floating popup
  useEffect(() => {
    if (!user) {
      prevBalanceRef.current = null;
      prevBalanceTypeRef.current = activeBalance;
      return;
    }
    
    const bal = currentBalance;
    
    // Only show notification if we didn't just switch the balance type
    if (prevBalanceTypeRef.current === activeBalance) {
      if (prevBalanceRef.current !== null && prevBalanceRef.current !== bal) {
        const delta = bal - prevBalanceRef.current;
        if (Math.abs(delta) >= 0.001) {
          const id = Date.now() + Math.random();
          setBalancePopups(prev => [...prev, { id, delta }]);
          setTimeout(() => {
            setBalancePopups(prev => prev.filter(p => p.id !== id));
          }, 1800);
        }
      }
    }
    
    prevBalanceRef.current = bal;
    prevBalanceTypeRef.current = activeBalance;
  }, [currentBalance, user, activeBalance]);

  const handleClaimRakeback = async () => {
    if (isClaimingRakeback || !user?.rakebackBalance || user.rakebackBalance < 0.01) return;
    
    setIsClaimingRakeback(true);
    try {
      const res = await userAPI.claimRakeback();
      if (res.data.success) {
        window._addToast?.({
          type: 'success',
          title: 'Rakeback Claimed',
          message: `Successfully claimed $${res.data.claimedAmount.toFixed(2)}!`
        });
        // Update local user state
        setUser(prev => ({
          ...prev,
          balance: res.data.newBalance,
          rakebackBalance: 0,
          lastRakebackClaimAt: new Date().toISOString()
        }));
      }
    } catch (err) {
      const errorMsg = apiHelpers.handleError(err, 'Failed to claim rakeback');
      window._addToast?.({
        type: 'error',
        title: 'Claim Failed',
        message: errorMsg
      });
    } finally {
      setIsClaimingRakeback(false);
    }
  };

  const cooldownActive = useMemo(() => {
    if (!user?.lastRakebackClaimAt) return false;
    const lastClaim = new Date(user.lastRakebackClaimAt).getTime();
    const now = Date.now();
    return now - lastClaim < 24 * 60 * 60 * 1000;
  }, [user?.lastRakebackClaimAt]);

  const [cooldownTimeLeft, setCooldownTimeLeft] = useState('');

  useEffect(() => {
    if (!cooldownActive || !user?.lastRakebackClaimAt) {
      setCooldownTimeLeft('');
      return;
    }
    
    const updateTimer = () => {
      const lastClaim = new Date(user.lastRakebackClaimAt).getTime();
      const nextClaim = lastClaim + 24 * 60 * 60 * 1000;
      const now = Date.now();
      const diff = nextClaim - now;
      
      if (diff <= 0) {
        setCooldownTimeLeft('');
        return;
      }
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setCooldownTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
    };
    
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [cooldownActive, user?.lastRakebackClaimAt]);

  const content = React.isValidElement(children)
    ? React.cloneElement(children, { layoutSearchTerm: search })
    : children;

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <header style={{
          height: '64px',
          background: '#0e1015',
          borderBottom: '1px solid #1a1c22',
          display: 'flex',
          alignItems: 'center',
          padding: '0 24px',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          justifyContent: 'space-between'
      }}>
          {/* Left Section: Nav Controls & Logo */}
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <button 
                      onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                      style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '8px' }}
                  >
                      <FaBars size={20} />
                  </button>
                  
                  <div style={{ display: 'flex', gap: '8px' }}>
                      <button 
                          style={{ 
                              background: activeKey !== 'rewards' ? 'rgba(255, 183, 3, 0.15)' : '#16191f', 
                              color: activeKey !== 'rewards' ? '#ffb703' : '#94a3b8', 
                              border: 'none', borderRadius: '4px', height: '36px', padding: '0 16px', 
                              fontSize: '13px', fontWeight: 900, cursor: 'pointer', transition: 'all 0.2s' 
                          }}
                          onClick={() => navTo('/dashboard?tab=home')}
                      >
                          Games
                      </button>
                      <button 
                          style={{ 
                              background: activeKey === 'rewards' ? 'rgba(255, 183, 3, 0.15)' : '#16191f', 
                              color: activeKey === 'rewards' ? '#ffb703' : '#94a3b8', 
                              border: 'none', borderRadius: '4px', height: '36px', padding: '0 16px', 
                              fontSize: '13px', fontWeight: 900, cursor: 'pointer', transition: 'all 0.2s' 
                          }}
                          onClick={() => navTo('/dashboard?tab=rewards')}
                      >
                          Rewards
                      </button>
                  </div>

                  {/* Logo moved to the right of Rewards */}
                  <div 
                      onClick={() => navTo('/')}
                      style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', transition: 'transform 0.2s', marginLeft: '12px' }}
                      onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
                      onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                  >
                      <img src="/coinimg.png" alt="Logo" style={{ width: '24px', height: '24px', objectFit: 'contain' }} />
                      <div style={{ display: 'flex', alignItems: 'center', fontSize: '20px', fontWeight: '950', textTransform: 'uppercase', letterSpacing: '-0.5px', marginLeft: '10px' }}>
                          <span style={{ color: '#ffffff' }}>RUSTY</span>
                          <span style={{ color: '#ffb703', marginLeft: '2px' }}>COIN</span>
                      </div>
                  </div>
              </div>
          </div>

          {/* Center Section: Balance & Deposit */}
          <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {user && (
                  <div style={{ 
                      display: 'flex', background: '#16191f', borderRadius: '4px', 
                      padding: '2px', border: '1px solid #1a1c22', height: '40px', position: 'relative' 
                  }} ref={balanceRef}>
                      <div 
                          style={{ display: 'flex', alignItems: 'center', padding: '0 16px', gap: '8px', color: '#fff', fontSize: '14px', fontWeight: 900, cursor: 'pointer' }} 
                          onClick={() => setShowBalanceMenu(v => !v)}
                      >
                          <img src="/scrap.webp" style={{ width: '16px', filter: 'drop-shadow(0 0 4px #ffb703)' }} alt="" />
                          {currentBalance.toFixed(2)}
                      </div>
                      <button 
                          style={{ 
                              background: '#ffb703', border: 'none', color: '#000', padding: '0 20px', 
                              fontSize: '13px', fontWeight: 950, cursor: 'pointer', transition: 'all 0.2s', 
                              borderRadius: '2px' 
                          }} 
                          onClick={() => setIsDepositModalOpen(true)}
                      >
                          Deposit
                      </button>
                      
                      <AnimatePresence>
                          {showBalanceMenu && (
                              <motion.div
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, y: 10 }}
                                  style={{
                                      position: 'absolute',
                                      top: 'calc(100% + 8px)',
                                      left: '50%',
                                      transform: 'translateX(-50%)',
                                      width: '220px',
                                      background: '#16191f',
                                      borderRadius: '6px',
                                      border: '1px solid #1a1c22',
                                      zIndex: 1000,
                                      boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                                      padding: '8px'
                                  }}
                              >
                                  <div style={{ padding: '8px 12px', color: '#6b7280', fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Switch Balance</div>
                                  <button
                                      onClick={() => { setActiveBalance('main'); setShowBalanceMenu(false); }}
                                      style={{
                                          width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '12px',
                                          background: activeBalance === 'main' ? 'rgba(255, 183, 3, 0.1)' : 'transparent',
                                          border: 'none', color: '#fff', fontSize: '13px', fontWeight: 700, cursor: 'pointer', borderRadius: '4px', textAlign: 'left'
                                      }}
                                  >
                                      <img src="/scrap.webp" style={{ width: '16px' }} alt="" />
                                      <div style={{ flex: 1 }}>
                                          <div style={{ color: activeBalance === 'main' ? '#fff' : '#9ca3af' }}>Main Balance</div>
                                          <div style={{ fontSize: '11px', color: '#6b7280' }}>${(user.balance || 0).toFixed(2)}</div>
                                      </div>
                                      {activeBalance === 'main' && <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ffb703' }} />}
                                  </button>
                                  <button
                                      onClick={() => { setActiveBalance('promo'); setShowBalanceMenu(false); }}
                                      style={{
                                          width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '12px',
                                          background: activeBalance === 'promo' ? 'rgba(255, 183, 3, 0.1)' : 'transparent',
                                          border: 'none', color: '#fff', fontSize: '13px', fontWeight: 700, cursor: 'pointer', borderRadius: '4px', marginTop: '4px', textAlign: 'left'
                                      }}
                                  >
                                      <img src="/scrap.webp" style={{ width: '16px', filter: 'hue-rotate(45deg)' }} alt="" />
                                      <div style={{ flex: 1 }}>
                                          <div style={{ color: activeBalance === 'promo' ? '#fff' : '#9ca3af' }}>Promo Balance</div>
                                          <div style={{ fontSize: '11px', color: '#6b7280' }}>${(user.promoBalance || 0).toFixed(2)}</div>
                                      </div>
                                      {activeBalance === 'promo' && <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ffb703' }} />}
                                  </button>
                              </motion.div>
                          )}
                      </AnimatePresence>
                  </div>
              )}
          </div>

          {/* Right Section: Utility Icons & Profile */}
          <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px', paddingRight: '40px' }}>
              {user ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <button 
                          style={{ width: '36px', height: '36px', borderRadius: '50%', background: (activeRightPanel === 'vip' && isChatOpen) ? '#ffb703' : '#16191f', border: 'none', color: (activeRightPanel === 'vip' && isChatOpen) ? '#000' : '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s' }}
                          onClick={() => { 
                              if (activeRightPanel === 'vip' && isChatOpen) {
                                  setIsChatOpen(false);
                              } else {
                                  setActiveRightPanel('vip');
                                  setIsChatOpen(true);
                              }
                          }}
                      >
                          <FaCrown size={16} />
                      </button>
                      <button 
                          style={{ width: '36px', height: '36px', borderRadius: '50%', background: (activeRightPanel === 'chat' && isChatOpen) ? '#ffb703' : '#16191f', border: 'none', color: (activeRightPanel === 'chat' && isChatOpen) ? '#000' : '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s' }} 
                          onClick={() => { 
                              if (activeRightPanel === 'chat' && isChatOpen) {
                                  setIsChatOpen(false);
                              } else {
                                  setActiveRightPanel('chat');
                                  setIsChatOpen(true);
                              }
                          }}
                      >
                          <FaComments size={16} />
                      </button>
                      <div ref={notificationsRef} style={{ position: 'relative' }}>
                          <button 
                              style={{ width: '36px', height: '36px', borderRadius: '50%', background: isNotificationsOpen ? '#ffb703' : '#16191f', border: 'none', color: isNotificationsOpen ? '#000' : '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s' }}
                              onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                          >
                              <FaBell size={16} />
                          </button>
                          <AnimatePresence>
                              {isNotificationsOpen && (
                                  <motion.div 
                                      initial={{ opacity: 0, y: 10 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      exit={{ opacity: 0, y: 10 }}
                                      style={{ position: 'absolute', top: 'calc(100% + 10px)', right: 0, width: '280px', background: '#16191f', borderRadius: '6px', border: '1px solid #1a1c22', zIndex: 100, overflow: 'hidden', boxShadow: '0 10px 25px rgba(0,0,0,0.5)' }}
                                  >
                                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #1a1c22' }}>
                                          <span style={{ color: '#fff', fontSize: '13px', fontWeight: 800 }}>Notifications</span>
                                          <span style={{ color: '#6b7280', fontSize: '12px', cursor: 'pointer' }}>Clear</span>
                                      </div>
                                      <div style={{ padding: '24px 16px', textAlign: 'center', color: '#ffb703', fontSize: '13px', fontWeight: 700 }}>
                                          No unread notifications
                                      </div>
                                  </motion.div>
                              )}
                          </AnimatePresence>
                      </div>
                      <div ref={profileRef} style={{ position: 'relative' }}>
                          <button style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#16191f', border: isProfileMenuOpen ? '2px solid #ffb703' : '2px solid transparent', padding: 0, overflow: 'hidden', cursor: 'pointer', transition: 'all 0.2s' }} onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}>
                              <img src={user.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.username}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          </button>
                          <AnimatePresence>
                              {isProfileMenuOpen && (
                                  <motion.div
                                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                      animate={{ opacity: 1, y: 0, scale: 1 }}
                                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                      transition={{ duration: 0.15 }}
                                      style={{ position: 'absolute', top: 'calc(100% + 10px)', right: 0, width: '200px', background: '#16191f', borderRadius: '6px', border: '1px solid #1a1c22', zIndex: 100, overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.6)', padding: '6px' }}
                                  >
                                      {[
                                          { icon: <FaUser size={14} />, label: 'Profile', action: () => { navTo('/dashboard?tab=profile'); setIsProfileMenuOpen(false); } },
                                          { icon: <FaLock size={14} />, label: 'Vault', action: () => { setIsVaultOpen(true); setIsProfileMenuOpen(false); } },
                                          { icon: <FaChartLine size={14} />, label: 'Betting History', action: () => { navTo('/dashboard?tab=betting-history'); setIsProfileMenuOpen(false); } },
                                          { icon: <FaHistory size={14} />, label: 'Transactions', action: () => { navTo('/dashboard?tab=transactions'); setIsProfileMenuOpen(false); } },
                                          { icon: <FaUsers size={14} />, label: 'Affiliates', action: () => { navTo('/dashboard?tab=earn-with-us'); setIsProfileMenuOpen(false); } },
                                          { icon: <FaBoxes size={14} />, label: 'Your Cases', action: () => { navTo('/dashboard?tab=your-cases'); setIsProfileMenuOpen(false); } },
                                          { icon: <FaCog size={14} />, label: 'Settings', action: () => { navTo('/dashboard?tab=settings'); setIsProfileMenuOpen(false); } },
                                          { icon: <FaHeadset size={14} />, label: 'Live Support', action: () => { window.open('https://discord.gg/7dayErEGt7', '_blank'); setIsProfileMenuOpen(false); } },
                                      ].map((item, i) => (
                                          <button
                                              key={i}
                                              onClick={item.action}
                                              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: 'transparent', border: 'none', color: '#d1d5db', fontSize: '13px', fontWeight: 700, cursor: 'pointer', borderRadius: '4px', transition: 'all 0.15s', textAlign: 'left' }}
                                              onMouseEnter={e => { e.currentTarget.style.background = '#1a1c22'; e.currentTarget.style.color = '#fff'; }}
                                              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#d1d5db'; }}
                                          >
                                              <span style={{ color: '#6b7280', width: '18px', display: 'flex', justifyContent: 'center' }}>{item.icon}</span>
                                              {item.label}
                                          </button>
                                      ))}
                                      <div style={{ height: '1px', background: '#1a1c22', margin: '4px 0' }} />
                                      <button
                                          onClick={() => { localStorage.removeItem('token'); localStorage.removeItem('user'); window.location.href = '/dashboard'; }}
                                          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: 'transparent', border: 'none', color: '#ef4444', fontSize: '13px', fontWeight: 700, cursor: 'pointer', borderRadius: '4px', transition: 'all 0.15s', textAlign: 'left' }}
                                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'; }}
                                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                                      >
                                          <span style={{ width: '18px', display: 'flex', justifyContent: 'center' }}><FaSignOutAlt size={14} /></span>
                                          Logout
                                      </button>
                                  </motion.div>
                              )}
                          </AnimatePresence>
                      </div>
                  </div>
              ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <button
                          style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
                          onClick={() => { setIsAuthModalOpen(true); setAuthModalView('login'); }}
                      >
                          Login
                      </button>
                      <button
                          style={{ background: '#ffb703', border: 'none', color: '#000', padding: '8px 20px', borderRadius: '4px', fontSize: '13px', fontWeight: 900, cursor: 'pointer', transition: 'all 0.2s' }}
                          onClick={() => { setIsAuthModalOpen(true); setAuthModalView('register'); }}
                          onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.1)'; e.currentTarget.style.boxShadow = '0 0 15px rgba(255, 183, 3, 0.3)'; }}
                          onMouseLeave={e => { e.currentTarget.style.filter = 'brightness(1)'; e.currentTarget.style.boxShadow = 'none'; }}
                      >
                          Sign up
                      </button>
                  </div>
              )}
          </div>
      </header>
      <div className="rc-layout" style={{ flex: 1, overflow: 'hidden' }}>
        <aside className={`rc-sidebarLeft ${isSidebarOpen ? 'rc-open' : ''} ${isDesktopSidebarCollapsed ? 'rc-desktop-closed' : ''}`} style={{ padding: '0', height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', background: '#0e1015', borderRight: '1px solid #1a1c22' }}>
          
          {/* Scrollable Navigation Area */}
          <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
          <div 
             className="rc-sidebarCategoryHeader"
             style={{ padding: '10px 16px 6px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
            onClick={() => setIsGamesCollapsed(!isGamesCollapsed)}
          >
             <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#ffb703', fontSize: '13px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                 <FaGamepad size={16} /> <span className="rc-linkText rc-categoryText" style={{ color: '#fff' }}>GAMES</span>
             </div>
             <span className="rc-linkText rc-categoryArrow" style={{ color: '#6b7280', fontSize: '10px', transition: 'transform 0.2s', transform: isGamesCollapsed ? 'rotate(0deg)' : 'rotate(180deg)' }}>
                 <FaCaretDown size={12} />
             </span>
          </div>

          <AnimatePresence>
            {!isGamesCollapsed && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} style={{ overflow: 'hidden', flexShrink: 0 }}>
                  <button type="button" data-tooltip="Cases" className={`rc-sideNavLink ${activeKey === 'cases' ? 'rc-active' : ''}`} onClick={() => navTo('/cases')}>
                     <span className="rc-iconWrap" ><FaBoxOpen size={18} /></span>
                     <span className="rc-linkText">Cases</span>
                  </button>
                  <button type="button" data-tooltip="Battles" className={`rc-sideNavLink ${activeKey === 'case-battles' ? 'rc-active' : ''}`} onClick={() => navTo('/case-battles')}>
                     <span className="rc-iconWrap" ><GiCrossedSwords size={18} /></span>
                     <span className="rc-linkText">Battles</span>
                  </button>
                  <button type="button" data-tooltip="Coinflip" className={`rc-sideNavLink ${activeKey === 'coinflip' ? 'rc-active' : ''}`} onClick={() => navTo('/coinflip')}>
                     <span className="rc-iconWrap" ><FaCoins size={18} /></span>
                     <span className="rc-linkText">Coinflip</span>
                  </button>
                  <button type="button" data-tooltip="Jackpot" className={`rc-sideNavLink ${activeKey === 'jackpot' ? 'rc-active' : ''}`} onClick={() => navTo('/jackpot')}>
                     <span className="rc-iconWrap" ><FaSyncAlt size={18} /></span>
                     <span className="rc-linkText">Jackpot</span>
                  </button>
                  <button type="button" data-tooltip="Bust" className={`rc-sideNavLink ${activeKey === 'bust' ? 'rc-active' : ''}`} onClick={() => navTo('/bust')}>
                     <span className="rc-iconWrap" ><FaChartLine size={18} /></span>
                     <span className="rc-linkText">Bust</span>
                  </button>

                </motion.div>
            )}
          </AnimatePresence>

          <div className="rc-sidebarDivider" style={{ height: '1px', background: '#1a1c22', margin: '12px' }} />

          <button type="button" data-tooltip="Rewards" className={`rc-sideNavLink ${activeKey === 'rewards' ? 'rc-active' : ''}`} onClick={() => navTo('/dashboard?tab=rewards')}>
             <span className="rc-iconWrap" ><FaGift size={18} /></span>
             <span className="rc-linkText" style={{ color: '#fff', fontWeight: 900, textTransform: 'uppercase', fontSize: '13px' }}>REWARDS</span>
          </button>
          <button type="button" data-tooltip="Promo Code" className="rc-sideNavLink" onClick={() => setIsRedeemModalOpen?.(true)}>
             <span className="rc-iconWrap" ><FaTicketAlt size={18} /></span>
             <span className="rc-linkText" style={{ color: '#fff', fontWeight: 900, textTransform: 'uppercase', fontSize: '13px' }}>PROMO CODE</span>
          </button>


          <div className="rc-sidebarDivider" style={{ height: '1px', background: '#1a1c22', margin: '12px' }} />

          <div 
            className="rc-sidebarCategoryHeader"
            style={{ padding: '12px 16px 8px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
            onClick={() => setIsProfileCollapsed(!isProfileCollapsed)}
          >
             <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#ffb703', fontSize: '13px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                 <FaUser size={16} /> <span className="rc-linkText rc-categoryText" style={{ color: '#fff' }}>PROFILE</span>
             </div>
             <span className="rc-linkText rc-categoryArrow" style={{ color: '#6b7280', fontSize: '10px', transition: 'transform 0.2s', transform: isProfileCollapsed ? 'rotate(0deg)' : 'rotate(180deg)' }}>
                 <FaCaretDown size={12} />
             </span>
          </div>

          <AnimatePresence>
            {!isProfileCollapsed && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} style={{ overflow: 'hidden', flexShrink: 0 }}>
                  <button type="button" data-tooltip="Vault" className="rc-sideNavLink" onClick={() => setIsVaultOpen(true)}>
                     <span className="rc-iconWrap"><FaLock size={16} /></span>
                     <span className="rc-linkText" style={{ fontWeight: 800 }}>Vault</span>
                  </button>
                  <button type="button" data-tooltip="Affiliates" className={`rc-sideNavLink ${activeKey === 'earn-with-us' ? 'rc-active' : ''}`} onClick={() => navTo('/dashboard?tab=earn-with-us')}>
                     <span className="rc-iconWrap"><FaUsers size={16} /></span>
                     <span className="rc-linkText" style={{ fontWeight: 800 }}>Affiliates</span>
                  </button>
                  <button type="button" data-tooltip="Settings" className={`rc-sideNavLink ${activeKey === 'settings' ? 'rc-active' : ''}`} onClick={() => navTo('/dashboard?tab=settings')}>
                     <span className="rc-iconWrap"><FaCog size={16} /></span>
                     <span className="rc-linkText" style={{ fontWeight: 800 }}>Settings</span>
                  </button>
                  <button type="button" data-tooltip="Live Support" className="rc-sideNavLink" onClick={() => { 
                    if (window.Tawk_API) {
                      window.Tawk_API.toggle(); 
                    } else { 
                      window._addToast?.({
                        type: 'success',
                        title: 'Support',
                        message: 'Live support is loading...'
                      });
                    }
                  }}>
                     <span className="rc-iconWrap"><FaHeadset size={18} /></span>
                     <span className="rc-linkText" style={{ fontWeight: 800 }}>Live support</span>
                  </button>
                  {(user?.isAdmin || user?.roles?.includes('admin')) && (
                    <button type="button" data-tooltip="Admin Panel" className={`rc-sideNavLink ${activeKey === 'admin' ? 'rc-active' : ''}`} onClick={() => navTo('/admin')}>
                       <span className="rc-iconWrap"><FaShieldAlt size={18} style={{ color: '#ef4444' }} /></span>
                       <span className="rc-linkText" style={{ fontWeight: 800, color: '#ef4444' }}>Admin Panel</span>
                    </button>
                  )}
                </motion.div>
            )}
          </AnimatePresence>
          </div>
          <div className="rc-sidebarFooter" style={{ padding: '16px', borderTop: '1px solid #1a1c22', display: 'flex', gap: '8px', marginTop: 'auto' }}>
                  <button className="rc-footerRewardsBtn" style={{ flex: 1, background: '#ffb703', color: '#000', padding: '12px', borderRadius: '4px', border: 'none', fontWeight: 800, fontSize: '12px', cursor: 'pointer' }} onClick={() => navTo('/dashboard?tab=rewards')}>
                      Open your rewards
                  </button>
                  <button className="rc-footerIconBtn" style={{ width: '40px', background: '#1a1c22', color: '#fff', borderRadius: '4px', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }} onClick={() => window.open('https://discord.gg/7dayErEGt7', '_blank')}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3333-.9555 2.419-2.1569 2.419zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3333-.946 2.419-2.1568 2.419z"/></svg>
                  </button>
              </div>
        </aside>

        <main className="rc-mainContent" id="mainContent" style={{ background: '#0e1015' }}>
          <div className="rc-contentWrap" style={{ display: 'flex', flexDirection: 'column', minHeight: '100%', position: 'relative' }}>
            <AnimatePresence mode="popLayout">
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.1, ease: 'easeOut' }}
                style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
              >
                {children}
                {activeKey !== 'admin' && <FrontpageFooter />}
              </motion.div>
            </AnimatePresence>
          </div>
        </main>

        {!isTosOrPrivacy && (
          <>
            <div 
              className={`rc-sidebarRightWrapper ${isChatOpen ? 'is-open' : ''}`}
              style={{ overflowY: 'auto' }}
            >
                {activeRightPanel === 'chat' && (
                    <>
                        <div className="rc-chatMobileHeader">
                           <span>Global Chat</span>
                           <button className="rc-chatClose" onClick={() => setIsChatOpen(false)}><FaTimes /></button>
                        </div>
                        <ChatSidebar mutedUsers={mutedUsers} setMutedUsers={setMutedUsers} />
                    </>
                )}
                {activeRightPanel === 'vip' && (
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#0e1015', color: '#fff', overflowY: 'auto' }}>
                        <div style={{ padding: '24px', textAlign: 'center', borderBottom: '1px solid #1a1c22', background: 'linear-gradient(180deg, #16191f 0%, #0e1015 100%)' }}>
                            <div style={{ fontSize: '14px', fontWeight: 800, color: '#fff' }}>VIP</div>
                            <div style={{ fontSize: '32px', fontWeight: 900, color: '#ffb703', fontStyle: 'italic', letterSpacing: '-1px', lineHeight: 1 }}>REWARDS</div>
                        </div>
                        <div style={{ padding: '16px' }}>
                            <div style={{ background: '#16191f', borderRadius: '6px', padding: '16px', marginBottom: '16px', border: '1px solid #1a1c22' }}>
                                <div style={{ fontSize: '14px', fontWeight: 900, color: '#fff', fontStyle: 'italic', textAlign: 'center', marginBottom: '12px' }}>
                                    GET <span >REWARDED</span> FOR<br/>EVERY DOLLAR YOU BET
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#64748b', fontWeight: 700, marginBottom: '6px' }}>
                                    <span>Your VIP Progress</span>
                                    <span>{(user?.level?.progress || 0).toFixed(2)}%</span>
                                </div>
                                <div style={{ height: '8px', background: '#0e1015', borderRadius: '4px', marginBottom: '8px', overflow: 'hidden' }}>
                                    <div style={{ width: `${user?.level?.progress || 0}%`, height: '100%', background: '#ffb703' }} />
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ fontSize: '10px', background: '#1f242d', padding: '2px 6px', borderRadius: '4px', fontStyle: 'italic', fontWeight: 800, color: '#94a3b8' }}>LVL {user?.level?.current || 1}</span>
                                    <span style={{ fontSize: '10px', background: '#ffb703', padding: '2px 6px', borderRadius: '4px', fontStyle: 'italic', fontWeight: 800, color: '#000' }}>LVL {((user?.level?.current || 1) < 10) ? 10 : ((user?.level?.current || 1) + 10)}</span>
                                </div>
                            </div>
                             <div style={{ background: '#16191f', borderRadius: '6px', padding: '16px', marginBottom: '12px', border: '1px solid #1a1c22' }}>
                                <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', alignItems: 'center' }}>
                                    <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: 'rgba(255, 183, 3, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <FaGift size={20} color="#ffb703" />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: '13px', fontWeight: 800, color: '#fff' }}>Instant rakeback</div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <img src="/scrap.webp" style={{ width: '12px' }} alt="" />
                                            <span style={{ fontSize: '14px', fontWeight: 900, color: '#ffb703' }}>{(user?.rakebackBalance || 0).toFixed(2)}</span>
                                        </div>
                                    </div>
                                </div>
                                <button 
                                    onClick={handleClaimRakeback}
                                    disabled={isClaimingRakeback || cooldownActive || !user?.rakebackBalance || user.rakebackBalance < 0.01}
                                    style={{ 
                                        width: '100%', 
                                        padding: '12px', 
                                        background: (isClaimingRakeback || cooldownActive || !user?.rakebackBalance || user.rakebackBalance < 0.01) ? '#1a1c22' : '#ffb703', 
                                        border: (cooldownActive) ? '1px solid rgba(255, 183, 3, 0.1)' : 'none',
                                        color: (isClaimingRakeback || cooldownActive || !user?.rakebackBalance || user.rakebackBalance < 0.01) ? '#6b7280' : '#000', 
                                        borderRadius: '4px', 
                                        fontWeight: 900, 
                                        cursor: (isClaimingRakeback || cooldownActive || !user?.rakebackBalance || user.rakebackBalance < 0.01) ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.2s',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px',
                                        fontSize: '13px'
                                    }}
                                >
                                    {isClaimingRakeback ? (
                                        <FaSpinner className="fa-spin" />
                                    ) : cooldownActive ? (
                                        <>
                                            <FaLock size={12} style={{ opacity: 0.6 }} />
                                            <span>{cooldownTimeLeft || 'Locked'}</span>
                                        </>
                                    ) : (
                                        'Claim'
                                    )}
                                </button>
                            </div>

                        </div>
                    </div>
                )}
            </div>
          </>
        )}

        {isSidebarOpen && (
          <div 
            className="rc-overlay" 
            onClick={() => { setIsSidebarOpen(false); setIsChatOpen(false); }}
          />
        )}

        {/* Vault Modal Overlay */}
        <AnimatePresence>
            {isVaultOpen && (
                <div 
                  className="rc-modalOverlay" 
                  onClick={() => setIsVaultOpen(false)} 
                  style={{ zIndex: 9999, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="rc-modal" 
                        onClick={e => e.stopPropagation()}
                        style={{ width: '100%', maxWidth: '400px', background: '#16191f', padding: '24px', border: '1px solid #1a1c22', borderRadius: '6px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                            <h2 style={{ fontSize: '18px', color: '#fff', margin: 0, fontWeight: 900 }}>Coin vault</h2>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'transparent', padding: '4px 8px', borderRadius: '4px', border: '1px solid #ffb703' }}>
                                    <img src="/scrap.webp" alt="coins" style={{ width: '14px', height: '14px' }} />
                                    <span style={{ color: '#fff', fontSize: '12px', fontWeight: 800 }}>{user?.balance?.toFixed(2) || '0.00'}</span>
                                </div>
                                <FaTimes style={{ color: '#9ca3af', cursor: 'pointer', fontSize: '16px' }} onClick={() => setIsVaultOpen(false)} />
                            </div>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '24px', background: 'transparent', padding: '0', borderRadius: '6px' }}>
                            <div style={{ marginBottom: '0px', display: 'flex', justifyContent: 'center', position: 'relative' }}>
                                <img src="/safe.png" alt="safe" style={{ height: '160px', objectFit: 'contain', filter: 'drop-shadow(0 10px 15px rgba(0,0,0,0.5))' }} />
                            </div>
                            <div style={{ border: '1px solid #ffb703', borderRadius: '4px', padding: '6px 32px', fontSize: '12px', color: '#ffb703', fontWeight: 800, marginBottom: '8px', background: '#16191f', zIndex: 2, marginTop: '-12px' }}>
                                Vaulted
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(0,0,0,0.3)', padding: '8px 16px', borderRadius: '4px' }}>
                                <img src="/scrap.webp" alt="coins" style={{ width: '20px', height: '20px' }} />
                                <span style={{ color: '#fff', fontSize: '24px', fontWeight: 900 }}>0.00</span>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
                            <button 
                                style={{ flex: 1, padding: '12px', background: vaultTab === 'deposit' ? 'rgba(255, 183, 3, 0.15)' : '#1f2228', color: vaultTab === 'deposit' ? '#ffb703' : '#9ca3af', border: vaultTab === 'deposit' ? '1px solid rgba(255, 183, 3, 0.3)' : '1px solid transparent', borderRadius: '4px', fontSize: '13px', fontWeight: 800, cursor: 'pointer', transition: 'all 0.2s' }}
                                onClick={() => setVaultTab('deposit')}
                            >
                                Deposit
                            </button>
                            <button 
                                style={{ flex: 1, padding: '12px', background: vaultTab === 'withdraw' ? 'rgba(255, 183, 3, 0.15)' : '#1f2228', color: vaultTab === 'withdraw' ? '#ffb703' : '#9ca3af', border: vaultTab === 'withdraw' ? '1px solid rgba(255, 183, 3, 0.3)' : '1px solid transparent', borderRadius: '4px', fontSize: '13px', fontWeight: 800, cursor: 'pointer', transition: 'all 0.2s' }}
                                onClick={() => setVaultTab('withdraw')}
                            >
                                Withdraw
                            </button>
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                            <div style={{ fontSize: '11px', color: '#9ca3af', textAlign: 'center', marginBottom: '8px', fontWeight: 700 }}>Enter coin amount</div>
                            <div style={{ display: 'flex', alignItems: 'center', background: '#111318', border: '1px solid #1f2228', borderRadius: '8px', padding: '10px 12px' }}>
                                <img src="/scrap.webp" alt="coins" style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                                <input type="number" placeholder="Amount" style={{ flex: 1, background: 'transparent', border: 'none', color: '#fff', fontSize: '14px', outline: 'none', fontWeight: 600 }} />
                                <button style={{ background: '#1f2228', border: 'none', color: '#9ca3af', fontSize: '10px', fontWeight: 800, padding: '4px 8px', borderRadius: '4px', cursor: 'pointer' }}>MAX</button>
                            </div>
                        </div>

                        <button style={{ width: '100%', padding: '14px', background: '#4ade80', color: '#000', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 900, cursor: 'pointer', marginBottom: '24px' }}>
                            {vaultTab === 'deposit' ? 'Deposit' : 'Withdraw'}
                        </button>

                        <div style={{ textAlign: 'center' }}>
                            <h3 style={{ color: '#fff', fontSize: '16px', margin: '0 0 4px 0', fontWeight: 900 }}>Lock vault</h3>
                            <p style={{ color: '#9ca3af', fontSize: '11px', margin: '0 0 16px 0', fontWeight: 600 }}>Lock your vaulted coins for a certain amount of time</p>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <select style={{ flex: 1, background: '#1f2228', border: '1px solid #2d3748', color: '#fff', padding: '12px', borderRadius: '8px', outline: 'none', fontSize: '12px', fontWeight: 700, appearance: 'none' }}>
                                    <option>5 minutes</option>
                                    <option>1 hour</option>
                                    <option>1 day</option>
                                    <option>1 week</option>
                                </select>
                                <button style={{ padding: '12px 16px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <FaLock size={12} /> Lock vault
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default LayoutWrapper;
